import * as React from 'react';
import clsx from 'clsx';
import type { SurveyQuestion, MultiSelectAnswer } from '@/types/survey';

interface Props {
  question: SurveyQuestion;
  answer: MultiSelectAnswer | undefined;
  onChange: (ans: MultiSelectAnswer) => void;
}

function toggleSelection(selected: string[], id: string): string[] {
  const set = new Set(selected);
  if (set.has(id)) {
    set.delete(id);
  } else {
    set.add(id);
  }
  return Array.from(set);
}

export const MultiSelectQuestion: React.FC<Props> = ({ question, answer, onChange }) => {
  const selected = answer?.selected_options ?? [];

  if (!question.options || question.options.length === 0) {
    return <div className='text-gray-600'>No options available.</div>;
  }

  const onToggle = (id: string) => {
    const next = toggleSelection(selected, id);
    const max = question.max_selections ?? Infinity;

    if (next.length > max) return;

    onChange({
      question_id: question.id,
      question_type: 'multi_select',
      selected_options: next,
    });
  };

  const isSelected = (id: string) => selected.includes(id);

  return (
    <div className='flex flex-col gap-3'>
      {question.options.map((opt) => {
        const active = isSelected(opt.id);
        return (
          <button
            key={opt.id}
            type='button'
            onClick={() => onToggle(opt.id)}
            className={clsx(
              'flex w-full items-center gap-4 rounded-12 p-4 text-left transition-all',
              'border',
              active
                ? 'border-brand-300 bg-brand-50 shadow-sm'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            )}
          >
            <div
              className={clsx(
                'flex size-5 shrink-0 items-center justify-center rounded-md border',
                active ? 'border-brand-600 bg-brand-100' : 'border-gray-400 bg-white'
              )}
            >
              {active && <div className='size-3 rounded-sm bg-brand-600' />}
            </div>

            <div className='flex flex-1 items-center gap-3'>
              {opt.icon && <span className='text-xl'>{opt.icon}</span>}
              <span
                className={clsx(
                  'font-sans text-16 leading-[1.4]',
                  active ? 'font-medium text-brand-900' : 'font-normal text-gray-700'
                )}
              >
                {opt.text}
              </span>
            </div>
          </button>
        );
      })}

      {(question.min_selections || question.max_selections) && (
        <div className='mt-2 text-sm text-gray-600'>
          {typeof question.min_selections === 'number' && (
            <span className='mr-3'>Min: {question.min_selections}</span>
          )}
          {typeof question.max_selections === 'number' && question.max_selections !== Infinity && (
            <span>Max: {question.max_selections}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectQuestion;
